import { Settings2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/Domain/components/Shared/Button/Button';

// Explicit anchor id on the docs page (cross-repo contract, kept in sync with the docs PR).
const DISCORD_SEVERAL_AGENTS_DOCS_URL =
  'https://docs.quickchat.ai/channels/discord#several-ai-agents-one-server';
const CALLOUT_LINK_CLASS_NAME =
  'link-underline underline-offset-2 transition-opacity hover:opacity-70';
const CALLOUT_EMPHASIS = <strong key="emphasis" className="font-medium text-foreground" />;

export interface DiscordAdvancedCalloutProps {
  onOpenAdvanced: () => void;
  actionsLink?: { to: string; onClick: () => void };
}

/**
 * Secondary path out of the one-click sheet. De-emphasized (no card chrome, muted context,
 * tucked under a divider) so the vast majority take the one-click "Add to your Discord server"
 * flow, while keeping a clearly-clickable "Advanced setup" button for advanced users.
 */
export const DiscordAdvancedCallout = ({
  onOpenAdvanced,
  actionsLink,
}: DiscordAdvancedCalloutProps) => {
  const { t } = useTranslation(['integration']);

  return (
    <div className="mt-6 border-t border-border/50 pt-4" data-testid="discord-advanced-callout">
      <p className="text-sm font-medium text-muted-foreground">
        {t('integration:discord.oneclick.advanced_card.title')}
      </p>
      <p className="mt-1 mb-3 max-w-prose text-xs text-muted-foreground/90 text-pretty">
        <Trans
          t={t}
          i18nKey="integration:discord.oneclick.advanced_card.description"
          components={[
            <a
              key="docs"
              href={DISCORD_SEVERAL_AGENTS_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={CALLOUT_LINK_CLASS_NAME}
              data-testid="discord-advanced-callout-docs-link"
            />,
            CALLOUT_EMPHASIS,
          ]}
        />
      </p>
      {actionsLink && (
        <div data-testid="discord-advanced-callout-actions">
          <p className="text-sm font-medium text-muted-foreground">
            {t('integration:discord.oneclick.advanced_card.actions_title')}
          </p>
          <p className="mt-1 mb-3 max-w-prose text-xs text-muted-foreground/90 text-pretty">
            <Trans
              t={t}
              i18nKey="integration:discord.oneclick.advanced_card.actions_description"
              components={[
                <Link
                  key="actions"
                  to={actionsLink.to}
                  onClick={actionsLink.onClick}
                  className={CALLOUT_LINK_CLASS_NAME}
                  data-testid="discord-advanced-callout-actions-link"
                />,
                CALLOUT_EMPHASIS,
              ]}
            />
          </p>
        </div>
      )}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full cursor-pointer"
        onClick={onOpenAdvanced}
        data-testid="discord-open-advanced-button"
      >
        <Settings2 className="size-4" />
        {t('integration:discord.oneclick.advanced_link')}
      </Button>
    </div>
  );
};

export default DiscordAdvancedCallout;
